import React, { useState, useMemo, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { socket } from "../utils/socket";
import { useDashboard } from "./dashboardStore";

// API base URL from environment variables
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const UserAttendance = () => {
	// State variables
	const [records, setRecords] = useState([]); // Stores attendance records
	const [search, setSearch] = useState(""); // Search term for filtering
	const [datePage, setDatePage] = useState(0); // Pagination index for dates
	const [showModal, setShowModal] = useState(false); // Toggle Add User modal
	const [newUser, setNewUser] = useState({ userName: "", role: "" }); // New user form
	const [errors, setErrors] = useState({}); // Form validation errors
	const { accounts } = useMsal(); // MSAL accounts for logged-in user
	const [loading, setLoading] = useState(false); // Loading state for fetches

	const todayStr = new Date().toISOString().split("T")[0]; // Today's date in YYYY-MM-DD

	const [showAbsentModal, setShowAbsentModal] = useState(false); // Toggle Absent Reason modal
	const [currentAbsentUser, setCurrentAbsentUser] = useState(null); // User for whom reason is being added
	const [absentReason, setAbsentReason] = useState({ type: "", otherText: "" }); // Absent reason form


    // Subscribe to dashboard store slice
    const attendanceDat = useDashboard(state => state.data?.attendance);
    
    useEffect(() => {
      if (!attendanceDat) return;
    
      const fetchData = async () => {
        try {
        // last 7 days (newest 7 days)
        const last7DaysRecords = attendanceDat.records.slice(0, 8 * attendanceDat.totalUsers);

          setRecords(last7DaysRecords); // if you want to store it
        } catch (err) {
          console.error("Error processing attendanceDat requests:", err);
          setRecords([]);
        }
      };
    
      fetchData();
    }, [attendanceDat]);



	// Reset pagination when records change
	useEffect(() => {
		setDatePage(0);
	}, [records]);
	
	
		// Socket updates
	useEffect(() => {
		if (!socket) return;

		const handleAlertAttendance = (data) => {
			if (data.type === "reasonPosted") { // newReason
                
                setRecords((prev) =>
                  prev.map((r) => {
                    const isMatch =
                      r.id === Number(data.newReason.userId) &&
                      new Date(r.attendanceDate).toISOString().split("T")[0] ===
                      new Date(data.newReason.date).toISOString().split("T")[0];
 
                    return isMatch ? { ...r, reason: data.newReason.reason } : r;
                  })
                );
			}
			if (data.type === "presentPosted") { // newPresent
                                
    			const normalize = (d) => new Date(d).toISOString().split("T")[0];
    
    			setRecords((prev) =>
    				prev.map((r) =>
    					r.id === Number(data.newPresent.userId) && 
    					normalize(r.attendanceDate) === normalize(data.newPresent.date)
    						? {
    								...r,
    								status: data.newPresent.status,
    								loggedName: data.newPresent.loggedName,
    								loginTime: data.newPresent.loginTime,
    						  }
    						: r
    				)
    			);
			}
			
		};

		socket.on("alertAttendance", handleAlertAttendance);

		return () => {
			socket.off("alertAttendance", handleAlertAttendance);
		};
	}, []);

	// Format date to readable string
	const formatDate = (dateStr) =>
		new Date(dateStr).toLocaleDateString("en-GB", {
			weekday: "long",
			day: "numeric",
			month: "long",
			year: "numeric",
		});

	// Get day name (Monday, Tuesday...)
	const formatDayName = (dateStr) =>
		new Date(dateStr).toLocaleDateString("en-GB", { weekday: "long" });

	const isSunday = (dateStr) => formatDayName(dateStr) === "Sunday";

	// Group records by date and apply search filter
	const groupedByDate = useMemo(() => {
		try {
			const recs = Array.isArray(records) ? records : [];
			const filtered = recs.filter((r) =>
				(r.userName?.toLowerCase() ?? "").includes(search.toLowerCase())
			);

			const groups = {};
			filtered.forEach((r) => {
				const date = r.attendanceDate ?? "Unknown";
				if (!groups[date]) groups[date] = [];
				groups[date].push(r);
			});

			const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
			return sortedDates.map((date) => ({ date, users: groups[date] }));
		} catch (err) {
			console.error("Error grouping records:", err);
			return [];
		}
	}, [records, search]);

	const currentGroup = groupedByDate[datePage]; // Current date group for pagination

	// Pagination handlers
	const handleNextDate = () =>
		setDatePage((p) => Math.min(p + 1, groupedByDate.length - 1));
	const handlePrevDate = () => setDatePage((p) => Math.max(p - 1, 0));

	// Delete user and their attendance records
	const handleDelete = async (id) => {
		try {
			await fetch(`${apiBaseUrl}/api/attendance/user/${id}`, { method: "DELETE" });
			const res = await fetch(`${apiBaseUrl}/api/users/${id}`, { method: "DELETE" });
			if (!res.ok) throw new Error("Failed to delete user");

			setRecords((prev) => prev.filter((r) => r.id !== id));
		} catch (err) {
			console.error(err);
			alert("Failed to delete user. Make sure attendance is cleared first.");
		}
	};

	// Mark a user present for a given date
	const handleMarkPresent = async (userId, date) => {
		try {
			const loggedName = accounts[0]?.name || "Unknown";
			const loginTime = new Date().toISOString();

			const res = await fetch(`${apiBaseUrl}/api/attendance`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userId, loggedName, status: "Present", loginTime, date }),
			});

		} catch (err) {
			console.error(err);
			alert("Failed to mark present. Try again.");
		}
	};

	// Add new user
	const handleAddUser = async () => {
		// Validate inputs
		if (!newUser.userName || !newUser.role) {
			setErrors({
				userName: !newUser.userName,
				role: !newUser.role,
			});
			return;
		}

		try {
			const res = await fetch(`${apiBaseUrl}/api/users`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(newUser),
			});
			const addedUser = await res.json();

			setRecords((prev) => [
				...prev,
				{
					...addedUser,
					status: "Absent",
					loggedName: "",
					loginTime: null,
					attendanceDate: todayStr,
				},
			]);
			setShowModal(false);
			setNewUser({ userName: "", role: "" });
			setErrors({});
			await fetchRecords();
		} catch (err) {
			console.error(err);
			alert("Failed to add user. Try again.");
		}
	};

	return (
		<div className="w-full  p-4">
			{/* Search + Add User */}
			<div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
				<input
					type="text"
					placeholder="Search by name..."
					className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setDatePage(0);
					}}
				/>
				
				{accounts[0]?.name === "Garvit Jairath" && 
				<button
					className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
					onClick={() => setShowModal(true)}
				>
					Add User
				</button>
				}
				
			</div>

			{/* Attendance Table or Holiday */}
			{loading ? (
				<p className="text-center text-blue-400 italic">Loading...</p>
			) : currentGroup ? (
				<>
					<h2 className="text-center font-semibold mb-2">
						{isSunday(currentGroup.date) ? (
							<span className="text-pink-600 text-xl font-bold">
								{formatDate(currentGroup.date)} — Holiday
							</span>
						) : (
							<>Attendance Date: {formatDate(currentGroup.date)}</>
						)}
					</h2>

					{/* Show attendance table if not Sunday */}
					{!isSunday(currentGroup.date) ? (
						<div className="overflow-x-auto rounded-md border border-gray-200">
							<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base text-center">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-3 py-4 font-medium text-gray-700 w-12">#</th>
										<th className="px-3 py-4 font-medium text-gray-700">Name</th>
										<th className="px-3 py-4 font-medium text-gray-700">Role</th>
										<th className="px-3 py-4 font-medium text-gray-700">Status</th>
										<th className="px-3 py-4 font-medium text-gray-700">Logger Name</th>
										<th className="px-3 py-4 font-medium text-gray-700">Login</th>
										<th className="px-3 py-4 font-medium text-gray-700">Reason</th>
										<th className="px-3 py-4 font-medium text-gray-700">Actions</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-100">
									{/* No records */}
									{currentGroup.users.length === 0 && (
										<tr>
											<td colSpan={7} className="px-3 py-4 text-gray-500 italic">
												No Records Found...
											</td>
										</tr>
									)}

									{/* Map users */}
									{currentGroup.users.map((r, idx) => (
										<tr
											key={r.id}
											className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white"
										>
											<td className="px-3 py-2">{idx + 1}</td>
											<td className="px-3 py-2">{r.userName}</td>
											<td className="px-3 py-2">{r.role}</td>
											<td
												className={`px-3 py-2 font-semibold ${
													r.status === "Absent"
														? "text-red-400"
														: "text-green-500"
												}`}
											>
												{r.status}
											</td>
											<td className="px-3 py-2">{r.loggedName || "-"}</td>
											<td className="px-3 py-2">
												{r.status === "Absent"
													? "-"
													: new Date(r.loginTime).toLocaleTimeString()}
											</td>
											<td
												className={`px-3 py-2 ${
													r.status === "Absent"
														? r.reason
															? "text-blue-600 font-bold"
															: "italic text-gray-500"
														: "-"
												}`}
											>
												{r.status === "Absent" ? r.reason || "N/A" : "-"}
											</td>

											{/* Action buttons */}
											<td className="px-3 py-2 flex justify-center gap-1">
												{/* Mark Present button */}
												{r.status !== "Present" &&
													new Date(r.attendanceDate)
														.toISOString()
														.split("T")[0] === todayStr && (
														<button
															className="px-2 py-1 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 cursor-pointer"
															onClick={() =>
																handleMarkPresent(
																	r.id,
																	new Date(r.attendanceDate)
																		.toISOString()
																		.split("T")[0]
																)
															}
														>
															Present
														</button>
													)}

												{/* Delete button */}
												{accounts[0]?.name === "Garvit Jairath" && 
												<button
													className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer"
													onClick={() => {
														if (
															window.confirm(
																`Are you sure you want to delete ${r.userName}?\nThis will delete ${r.userName} from all dates and users table.`
															)
														)
															handleDelete(r.id);
													}}
												>
													Delete
												</button>
                                                }
												{/* Absent Reason button */}
												{r.status === "Absent" && (
													<button
														className="px-2 py-1 text-sm text-white bg-yellow-600 rounded-md hover:bg-yellow-700 cursor-pointer"
														onClick={() => {
															setCurrentAbsentUser(r);
															setAbsentReason({ type: "", otherText: "" });
															setShowAbsentModal(true);
														}}
													>
														Reason
													</button>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<p className="text-center text-gray-500 italic mb-90">
							Enjoy your Sunday off! 🎉
						</p>
					)}

					{/* Date Pagination */}
					{groupedByDate.length > 1 && (
						<div className="flex items-center justify-center gap-2 mt-3">
							<button
								className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
								onClick={handlePrevDate}
								disabled={datePage === 0}
							>
								Prev
							</button>
							<span className="text-sm text-gray-700">
								Date {datePage + 1} of {groupedByDate.length}
							</span>
							<button
								className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
								onClick={handleNextDate}
								disabled={datePage === groupedByDate.length - 1}
							>
								Next
							</button>
						</div>
					)}
				</>
			) : (
				<p className="text-center text-gray-500 italic">No Records Found...</p>
			)}

			{/* Add User Modal */}
			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
						<h2 className="text-lg font-semibold mb-4 text-center">
							Add New User
						</h2>
						<div className="flex flex-col gap-3">
							<input
								type="text"
								placeholder="Username"
								className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${
									errors.userName ? "border-red-500" : "border-gray-300"
								}`}
								value={newUser.userName}
								onChange={(e) =>
									setNewUser({ ...newUser, userName: e.target.value })
								}
							/>
							<select
								className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${
									errors.role ? "border-red-500" : "border-gray-300"
								}`}
								value={newUser.role}
								onChange={(e) =>
									setNewUser({ ...newUser, role: e.target.value })
								}
							>
								<option value="">Select role</option>
								<option value="staff">Staff</option>
							</select>
						</div>
						<div className="flex justify-end gap-2 mt-4">
							<button
								className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
								onClick={() => {
									setShowModal(false);
									setErrors({});
								}}
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
								onClick={handleAddUser}
							>
								Add
							</button>
						</div>
						<button
							className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
							onClick={() => {
								setShowModal(false);
								setErrors({});
							}}
						>
							✕
						</button>
					</div>
				</div>
			)}

			{/* Absent Reason Modal */}
			{showAbsentModal && currentAbsentUser && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
						<h2 className="text-lg font-semibold mb-4 text-center">
							Absent Reason for {currentAbsentUser.userName}
						</h2>
						<div className="flex flex-col gap-3">
							<select
								className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
								value={absentReason.type}
								onChange={(e) =>
									setAbsentReason({ ...absentReason, type: e.target.value })
								}
							>
								<option value="">Select reason</option>
								<option value="Sick">Sick</option>
								<option value="Vacation">Vacation</option>
								<option value="Other">Other</option>
							</select>
							<p className="text-blue-500 font-semibold text-left text-base">
								You can change the reason anytime.
							</p>
							{absentReason.type === "Other" && (
								<input
									type="text"
									placeholder="Specify reason..."
									className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
									value={absentReason.otherText}
									onChange={(e) =>
										setAbsentReason({
											...absentReason,
											otherText: e.target.value,
										})
									}
								/>
							)}
						</div>
						<div className="flex justify-end gap-2 mt-4">
							<button
								className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
								onClick={() => setShowAbsentModal(false)}
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
								onClick={async () => {
									try {
										if (
											!absentReason.type ||
											(absentReason.type === "Other" && !absentReason.otherText)
										) {
											alert("⚠️ Please choose a reason!");
											return;
										}

										const savedReason =
											absentReason.type === "Other"
												? absentReason.otherText
												: absentReason.type;

										const res = await fetch(`${apiBaseUrl}/api/absences`, {
											method: "POST",
											headers: { "Content-Type": "application/json" },
											body: JSON.stringify({
												userId: currentAbsentUser.id,
												date: currentAbsentUser.attendanceDate,
												reason: savedReason,
											}),
										});

										if (!res.ok) throw new Error("Failed to save reason");

										setShowAbsentModal(false);
									} catch (err) {
										console.error(err);
										alert("Failed to save reason");
									}
								}}
							>
								Save
							</button>
						</div>
						<button
							className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
							onClick={() => setShowAbsentModal(false)}
						>
							✕
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default UserAttendance;
